const RECHARTS_BLOCK_PATTERN = /```recharts\s*\n([\s\S]*?)```/g;
const HEADING_LINE_PATTERN = /^#{1,6}\s+(?:\d+[.)]\s+)?(.+?)\s*$/;
const BOLD_HEADING_LINE_PATTERN = /^\*\*(.+?)\*\*\s*$/;

function stripAccents(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function normalizeHeaderForComparison(text: string): string {
    if (!text) return '';
    const cleaned = stripAccents(text.toLowerCase());
    return cleaned.replace(/[^a-z0-9]/g, '');
}

function extractNormalizedHeaderWords(text: string): string[] {
    if (!text) return [];
    const cleaned = stripAccents(text.toLowerCase());
    return cleaned.split(/[^a-z0-9]+/).filter(Boolean);
}

function stripInlineMarkdown(text: string): string {
    return text.replace(/\*\*(.+?)\*\*/g, '$1').trim();
}

function extractHeadingText(line: string): string | null {
    const stripped = line.trim();
    if (!stripped) return null;

    const headingMatch = stripped.match(HEADING_LINE_PATTERN);
    if (headingMatch) {
        return stripInlineMarkdown(headingMatch[1]);
    }

    const boldMatch = stripped.match(BOLD_HEADING_LINE_PATTERN);
    if (boldMatch) {
        return boldMatch[1].trim();
    }

    return null;
}

export function isRedundantChartHeader(outerHeader: string, chartTitle: string): boolean {
    if (!outerHeader || !chartTitle) return false;

    const normalizedOuter = normalizeHeaderForComparison(outerHeader);
    const normalizedChart = normalizeHeaderForComparison(chartTitle);
    if (normalizedOuter && normalizedOuter === normalizedChart) {
        return true;
    }

    const outerWords = extractNormalizedHeaderWords(outerHeader);
    const innerWords = extractNormalizedHeaderWords(chartTitle);
    if (!outerWords.length || outerWords.length >= innerWords.length) {
        return false;
    }

    let outerIdx = 0;
    for (const word of innerWords) {
        if (outerIdx < outerWords.length && word === outerWords[outerIdx]) {
            outerIdx += 1;
        }
    }

    return outerIdx === outerWords.length;
}

export function stripRedundantChartHeadings(text: string): string {
    if (!text || !text.includes('```recharts')) {
        return text;
    }

    const removals: Array<{ start: number; end: number }> = [];

    for (const match of text.matchAll(RECHARTS_BLOCK_PATTERN)) {
        const blockStart = match.index ?? -1;
        if (blockStart < 0) continue;

        let config: { title?: unknown };
        try {
            config = JSON.parse(match[1]);
        } catch {
            continue;
        }

        const chartTitle = config.title;
        if (typeof chartTitle !== 'string' || !chartTitle.trim()) {
            continue;
        }

        const prefix = text.slice(0, blockStart);
        const lines = prefix.split('\n');

        let lineIdx = lines.length - 1;
        while (lineIdx >= 0 && !lines[lineIdx].trim()) {
            lineIdx -= 1;
        }
        if (lineIdx < 0) continue;

        const headingText = extractHeadingText(lines[lineIdx]);
        if (!headingText || !isRedundantChartHeader(headingText, chartTitle)) {
            continue;
        }

        const lineStart = lines.slice(0, lineIdx).reduce((acc, line) => acc + line.length + 1, 0);
        removals.push({ start: lineStart, end: blockStart });
    }

    if (!removals.length) {
        return text;
    }

    let result = text;
    for (const { start, end } of removals.sort((a, b) => b.start - a.start)) {
        result = result.slice(0, start) + result.slice(end);
    }

    return result.trimEnd();
}
